<template>
  <div class="home-page">
    <h2>近期活動資訊</h2>
    
    <!-- Loading 狀態 -->
    <div v-if="loading" class="loading">
      <p>載入中...</p>
    </div>
    
    <!-- 錯誤訊息 -->
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="loadActivities">重新載入</button>
    </div>
    
    <!-- 無活動 -->
    <div v-else-if="activities.length === 0" class="no-data">
      <p>目前尚無活動資訊</p>
    </div>
    
    <!-- 活動列表 -->
    <ul v-else class="activities-list">
      <li v-for="activity in activities" :key="activity.id" class="activity-item">
        <h3>{{ activity.title }}</h3>
        <p class="description">{{ stripHtml(activity.description) }}</p>
        <div class="activity-meta">
          <p><strong>日期：</strong>{{ formatDate(activity.start_date || activity.activity_date) }} ~ {{ formatDate(activity.end_date) }}</p>
          <p><strong>類別：</strong>{{ formatCategory(activity.category) }}</p>
          <p>
            <strong>自主學習點數：</strong>{{ activity.learning_hours || 0 }}
            <span v-if="activity.needs_reflection && activity.reflection_points > 0" class="reflection-bonus">（繳交心得可再獲得 {{ activity.reflection_points }} 點）</span>
          </p>
          <p><strong>地點：</strong>{{ activity.location || '無' }}</p>
          <p v-if="activity.speaker"><strong>講者：</strong>{{ activity.speaker }}</p>
        </div>
        <div class="detail-row">
          <button @click="goToDetail(activity)" class="detail-btn">查看詳情 / 報名</button>
          <span class="reg-status">
            <template v-if="activity.max_participants">
              本活動接受 <strong>{{ activity.max_participants }}</strong> 人報名，目前已有 <strong>{{ activity.registration_count || 0 }}</strong> 人報名<b v-if="(activity.registration_count || 0) >= activity.max_participants" class="full-tag">（已額滿）</b>
            </template>
            <template v-else>
              目前已有 <strong>{{ activity.registration_count || 0 }}</strong> 人報名
            </template>
          </span>
        </div>
      </li>
    </ul>
    
    <!-- 分頁控制 -->
    <div v-if="activities.length > 0" class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1">上一頁</button>
      <span>第 {{ currentPage }} 頁</span>
      <button @click="nextPage" :disabled="activities.length < pageSize">下一頁</button>
    </div>
  </div>
</template>

<script>
import { getActivities } from '@/api.js'
import { categoryLabel } from '@/utils/category.js'

export default {
  name: 'Home',
  data() {
    return {
      activities: [],
      loading: false,
      error: null,
      currentPage: 1,
      pageSize: 10
    }
  },
  created() {
    this.loadActivities()
  },
  methods: {
    formatCategory(cat) {
      return categoryLabel(cat)
    },

    // 去除活動簡述的 HTML 標籤，卡片只顯示純文字摘要
    stripHtml(html) {
      if (!html) return ''
      try {
        const doc = new DOMParser().parseFromString(String(html), 'text/html')
        return (doc.body.textContent || '').trim()
      } catch (e) {
        return String(html).replace(/<[^>]*>/g, '')
      }
    },

    async loadActivities() {
      this.loading = true
      this.error = null
      
      try {
        const skip = (this.currentPage - 1) * this.pageSize
        const response = await getActivities({ 
          skip: skip, 
          limit: this.pageSize 
        })
        
        this.activities = response.data
        console.log(`載入了 ${this.activities.length} 個活動`)
        
      } catch (error) {
        console.error('載入活動列表失敗：', error)
        this.error = error.response?.data?.detail || '無法載入活動列表，請稍後再試'
      } finally {
        this.loading = false
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '待定'
      
      try {
        // 處理 ISO 格式日期
        const date = new Date(dateString)
        
        if (isNaN(date.getTime())) {
          console.warn('無效的日期格式:', dateString)
          return '日期格式錯誤'
        }
        
        // 使用 toLocaleString 確保正確的時區處理
        const formatted = date.toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        
        return formatted.replace(/\//g, '-')  // 統一使用 - 分隔符
        
      } catch (error) {
        console.error('Date parsing error:', error, 'for date:', dateString)
        return '日期解析失敗'
      }
    },
    
    goToDetail(activity) {
      this.$router.push(`/activity/${activity.id}`)
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.loadActivities()
      }
    },
    
    nextPage() {
      if (this.activities.length === this.pageSize) {
        this.currentPage++
        this.loadActivities()
      }
    }
  }
}
</script>

<style scoped>
.home-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

h2 {
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

/* Loading 和錯誤狀態 */
.loading, .error, .no-data {
  text-align: center;
  padding: 40px;
  color: #666;
}

.error {
  color: #dc3545;
}

.error button {
  margin-top: 10px;
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.error button:hover {
  background-color: #c82333;
}

/* 活動列表 */
.activities-list {
  list-style: none;
  padding: 0;
}

.activity-item {
  border: 1px solid #ddd;
  padding: 20px;
  margin-bottom: 15px;
  border-radius: 8px;
  background-color: #f9f9f9;
  transition: box-shadow 0.3s;
}

.activity-item:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.activity-item h3 {
  margin: 0 0 10px;
  color: #42b983;
  font-size: 20px;
}

.description {
  color: #666;
  margin-bottom: 15px;
  line-height: 1.5;
}

.activity-meta p {
  margin: 5px 0;
  font-size: 14px;
  color: #555;
}

.reflection-bonus {
  color: #2e7d32;
  font-size: 13px;
}

.detail-row {
  margin-top: 15px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.reg-status {
  font-size: 14px;
  color: #555;
}
.reg-status .full-tag {
  color: #c62828;
  font-weight: 700;
}
.detail-btn {
  margin-top: 0;
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.detail-btn:hover {
  background-color: #369f6c;
}

/* 分頁 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
}

.pagination button {
  padding: 8px 16px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.pagination button:hover:not(:disabled) {
  background-color: #5a6268;
}

.pagination button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .home-page {
    padding: 10px;
  }
  
  .activity-item {
    padding: 15px;
  }
  
  .activity-item h3 {
    font-size: 18px;
  }
  
  .activity-meta p {
    font-size: 13px;
  }
  
  .detail-btn {
    width: 100%;
  }
}
</style>